import React, { useState } from 'react';
import { GripVertical, Play, X } from 'lucide-react';
import { isGalleryVideoUrl } from '../utils/galleryMedia';

function reorderArray(arr, fromIndex, toIndex) {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return [...arr];
  const next = [...arr];
  const [removed] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, removed);
  return next;
}

const DEFAULT_HINT =
  'Drag to reorder. The first item is the listing cover. Photos and MP4/MOV/WebM videos are supported.';

/**
 * Draggable grid of image URLs (stay listing, room photos, etc.). First image is the cover where applicable.
 */
export default function DraggableStayImagesGrid({
  images,
  onReorder,
  onRemove,
  hintText = DEFAULT_HINT,
  altPrefix = 'Listing',
  gridClassName = 'grid grid-cols-2 md:grid-cols-4 gap-4',
  imageClassName = 'w-full h-32 object-cover rounded-lg',
  removeButtonClassName = 'absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity',
  hintClassName = 'text-sm text-gray-500 mb-3'
}) {
  const [dragIndex, setDragIndex] = useState(null);

  if (!images || images.length === 0) return null;

  return (
    <div>
      <p className={hintClassName}>{hintText}</p>
      <div className={gridClassName}>
        {images.map((image, index) => (
          <div
            key={`${index}-${image}`}
            draggable
            onDragStart={(e) => {
              setDragIndex(index);
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData('text/plain', String(index));
            }}
            onDragEnd={() => setDragIndex(null)}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
            }}
            onDrop={(e) => {
              e.preventDefault();
              const from = dragIndex;
              setDragIndex(null);
              if (from === null || from === index) return;
              onReorder(reorderArray(images, from, index));
            }}
            className={`relative group rounded-lg border-2 transition-colors ${
              dragIndex === index ? 'border-[#FF5E4C] ring-2 ring-[#FF5E4C]/25' : 'border-transparent'
            }`}
          >
            <div
              className="absolute top-2 left-2 z-10 flex items-center justify-center rounded bg-black/50 p-1 text-white cursor-grab active:cursor-grabbing touch-none"
              title="Drag to reorder"
            >
              <GripVertical className="w-4 h-4" strokeWidth={2} aria-hidden />
            </div>
            {index === 0 ? (
              <span className="absolute bottom-2 left-2 z-10 rounded bg-black/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                Cover
              </span>
            ) : null}
            {isGalleryVideoUrl(image) ? (
              <>
                <video
                  src={image}
                  className={imageClassName}
                  muted
                  playsInline
                  preload="metadata"
                  draggable={false}
                />
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/20">
                  <Play className="h-8 w-8 text-white fill-white" aria-hidden />
                </span>
              </>
            ) : (
              <img src={image} alt={`${altPrefix} ${index + 1}`} className={imageClassName} draggable={false} />
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(index);
              }}
              className={removeButtonClassName}
              aria-label={`Remove image ${index + 1}`}
            >
              <X className="h-4 w-4 shrink-0" strokeWidth={2} aria-hidden />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
